import React from "react";
import ReactDOM from "react-dom";
import ReactDOMServer from "react-dom/server";
import { RelayEnvironmentProvider } from "react-relay";

import { faH1, faH2 } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faBolt as faBoltFilled, faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { sortBy } from "lodash";
import { type DOMOutputSpec, Node as ProseMirrorNode, ResolvedPos } from "prosemirror-model";
import { EditorState, Plugin, TextSelection, Transaction } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView, type NodeView } from "prosemirror-view";
import { setAttr } from "redom";
import invariant from "tiny-invariant";
import { v4 } from "uuid";

import { type HeaderContentType, Header as ReactAIHeader } from "~/agendaBuilder";
import { DismissibleElementsContext } from "~/common/DismissibleElements";
import { ScreenSize } from "~/common/ViewportContext";
import { type DOMComponent, setStyles } from "~/lib/dom";
import { BlockActionMenu, getUser } from "~/lib/prose/blockActions";
import { ASSIGNABLE_TYPE_NAMES } from "~/lib/prose/plugins/assignable";
import { blockActionsEnabled } from "~/lib/prose/plugins/blockActions";
import { getContext, getCurrentUser } from "~/lib/prose/plugins/context";
import {
	AI_HEADERS_DECO_ORDER,
	BoltButton,
	deserializePromptAttrsFromDOMAttrs,
	getPromptMenuItems,
	PlusButton,
	promptAllRequired,
	promptAttrs,
	promptEnabled,
	promptExceptionUsers,
	promptHeadingsEnabled,
	promptRequiredUsers,
	promptResponseAttrs,
	serializePromptAttrstoDOMAttrs,
	userName,
} from "~/lib/prose/plugins/prompt";
import { clearTooltip, listFormat, setTooltip } from "~/lib/utils";
import { environment } from "~/models";
import {
	type AssignableDropdownMenuSections,
	getCommentMenuItem,
	getCopyLinkMenuItem,
	getCreatorMenuItem,
	getDeleteBlockMenu,
	getReactionMenuItem,
} from "~/notes/AssignableDropdown";
import { loadingBar } from "~/stream/LoadingNoteCard";

import { setBlockType } from "../commands";
import { NodeExtension } from "../extension";
import { aiProseKey, AITopicsExtension } from "../plugins/aiTopics";
import { getBlockBoundaryState } from "../plugins/blockBoundary";
import { CollapseExpandButton, isCollapsed, isCollapsible, setCollapsed } from "../plugins/collapse";
import { blockTypeShortcut } from "../plugins/inputRules";
import { AI_TOPICS_TEMPLATE, type CustomSectionConfigs } from "../snippet";
import type { getPosHandler } from "../types";
import { closestBlock, isVisuallyEmpty, nextBlockInsertionPos, nodeActive } from "../utils";
import { LIST_ITEM_TYPE_NAMES } from "./plaintext";

import type { NoteCard_viewer$data as Viewer } from "~/__generated__/NoteCard_viewer.graphql";

import styles from "../css/header.module.css";

// NOTE: ensure that the same constant in apps/notes/blocks_iterator.py is kept
// in sync with this
export const HEADING_MAX_LEVEL = 2;

const DecorateSubheader = new Plugin({
	props: {
		decorations: (state: EditorState) => {
			const decorations: Decoration[] = [];
			state.doc.descendants((node, pos, _parent) => {
				if (node.type.name.startsWith("header")) {
					let parentOffset = 0;
					for (let i = 0; i < node.childCount; i += 1) {
						const child = node.child(i);
						parentOffset += child.nodeSize;
						if (child.type.name === "hard_break") {
							decorations.push(Decoration.inline(pos + parentOffset, pos + node.nodeSize, { class: "subheader" }));
							break;
						}
					}
				}
				return false;
			});
			return DecorationSet.create(state.doc, decorations);
		},
	},
});

type AIHeaderData = HeaderContentType & {
	view: EditorView;
	position: number;
	showTooltip: boolean;
	viewer: Viewer;
};
export function AIHeader(
	data: AIHeaderData & {
		dismissibleElementsContext: React.ContextType<typeof DismissibleElementsContext>;
	},
) {
	const { id, content, subheader, showTooltip } = data;

	const onClick = React.useCallback(() => {
		const { view, position, id: blockId, content, subheader, talkingPoint } = data;

		if (!view.editable) return;

		const { state } = view;
		const { tr, schema } = state;
		const currentUser = getCurrentUser(state);

		if (!currentUser) throw new Error("Can't get current user");

		const nodeContent = [schema.text(content)];
		if (subheader.trim()) {
			nodeContent.push(schema.node("hard_break"), schema.text(subheader));
		}

		const node = schema.node(
			"header-one",
			{ placeholder: "AI-generated header", blockId, promptResponseTemplate: AI_TOPICS_TEMPLATE },
			nodeContent,
		);

		const $pos = tr.doc.resolve(position);
		const insertPos = nextBlockInsertionPos($pos, true);
		const aiTopicsPlugin = view.extensions.get(aiProseKey);
		invariant(aiTopicsPlugin instanceof AITopicsExtension);
		aiTopicsPlugin.insertInitialTalkingPointForHeader(blockId, content, talkingPoint, tr, insertPos, node, subheader);
	}, [data]);

	return (
		<RelayEnvironmentProvider environment={environment}>
			<DismissibleElementsContext.Provider value={data.dismissibleElementsContext}>
				<ReactAIHeader id={id} content={content} subheader={subheader} onClick={onClick} showTooltip={showTooltip} />
			</DismissibleElementsContext.Provider>
		</RelayEnvironmentProvider>
	);
}

type LoadingHeaderData = { isFirstBlock: boolean };

class LoadingHeader implements DOMComponent<LoadingHeaderData> {
	el: HTMLElement;
	data?: LoadingHeaderData;

	constructor(data: LoadingHeaderData) {
		this.el = document.createElement("div");
		this.el.classList.add(styles.loadingHeaderContainer);
		ReactDOM.render(
			<div className={styles.loadingHeader} style={data.isFirstBlock ? { marginTop: 0 } : {}}>
				<FontAwesomeIcon icon={faSparkles} className={styles.icon} />
				{loadingBar(50, styles.loadingHeaderBar)}
				{loadingBar(70, styles.loadingSubheaderBar)}
			</div>,
			this.el,
		);

		this.update(data);
	}

	update = (data: LoadingHeaderData) => {
		this.data = data;
	};
}

type HeaderComponentData = {
	node: ProseMirrorNode;
	view: EditorView;
	getPos: boolean | (() => number);
	inTemplate?: boolean;
	inNoteHistory?: boolean;
	inCustomSection?: boolean;
	customSectionConfigs?: CustomSectionConfigs;
	promptDisabled?: boolean;
};

const boltIconFilledMarkup = ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faBoltFilled} width={12} />);

class HeaderComponent implements DOMComponent<HeaderComponentData> {
	dom: HTMLElement;
	contentDOM: HTMLDivElement;
	contentWrapper: HTMLDivElement;
	blockActions?: BlockActionMenu;
	data?: HeaderComponentData;
	plusButton?: PlusButton;
	boltButton?: BoltButton;
	boltIconContainer: HTMLElement;
	collapsedSummary?: CollapsedHeaderSummary;

	// Can't use @custom-media variables here; this should be equivalent to
	// --max-md as defined in ~/css/media.css, which corresponds to when we hide
	// the drag handle.
	private maxMdMedia = matchMedia(`(max-width: ${ScreenSize.mobile - 2}px)`);

	constructor(data: HeaderComponentData) {
		this.dom = document.createElement(`h${data.node.attrs.level}`);
		this.contentDOM = document.createElement("div");
		this.contentDOM.style.setProperty("flex-grow", "1");
		this.contentDOM.style.setProperty("flex-shrink", "1");
		this.contentDOM.style.setProperty("flex-basis", "0");
		this.contentWrapper = document.createElement("div");
		this.contentWrapper.style.display = "flex";
		this.contentWrapper.style.flexWrap = "wrap";
		this.boltIconContainer = document.createElement("div");
		this.boltIconContainer.contentEditable = "false";
		this.boltIconContainer.classList.add("boltIcon");
		this.boltIconContainer.innerHTML = boltIconFilledMarkup;
		this.maxMdMedia.addEventListener("change", () => this.data && this.updateMobileCollapseExpandButton(this.data));
		this.update(data);
	}

	get el() {
		return this.dom;
	}

	canUpdate = (data: HeaderComponentData) => {
		if (data.node.type !== this.data?.node.type) return false;
		if (data.node.attrs.level !== this.data?.node.attrs.level) return false;
		return true;
	};

	update = (data: HeaderComponentData) => {
		if (!this.data) {
			this.ensureDOMStructure();
		}
		const { dom, contentDOM, contentWrapper } = this;
		const { node } = data;
		dom.setAttribute("pmblock", "");
		dom.setAttribute("data-block-id", node.attrs["blockId"]);
		dom.setAttribute("data-block-type", node.attrs["level"]);
		dom.setAttribute("data-creator", node.attrs["creator"]);
		contentDOM.setAttribute("nodeviewcontent", "");
		contentDOM.setAttribute("pmcontent", "");
		// Ensure that the heading node has some width, which will make the
		// collapsed summary wrap to the next line. Otherwise the collapsed section
		// summary will overlap the placeholder and the cursor won't show up. We'll
		// also set the min-height which is needed when there's a subheading but no
		// heading.
		if (isCollapsed(data.view.state, node)) {
			contentDOM.style.minWidth = "1px";
			// This should match the line height in editor.global.module.less
			contentDOM.style.minHeight = node.attrs.level === "1" ? "1.18em" : "22px";
		} else {
			contentDOM.style.minWidth = "";
			contentDOM.style.minHeight = "";
		}
		this.updateMobileCollapseExpandButton(data);
		contentWrapper.setAttribute("pmcontentWrapper", "");
		this.addPlusButton(data);
		this.addBoltButton(data);
		// this.addConfigureButton(data);
		this.updateBlockActions(data);
		this.showHideBoltIcon(data);
		this.updateCollapsedSummary(data);
		this.data = data;
	};

	deinit() {
		this.blockActions?.deinit();
	}

	private updateCollapsedSummary(data: HeaderComponentData) {
		if (isCollapsed(data.view.state, data.node)) {
			const collapsedSummaryData = { node: data.node, view: data.view };
			if (!this.collapsedSummary) {
				this.collapsedSummary = new CollapsedHeaderSummary(collapsedSummaryData);
				this.contentWrapper.appendChild(this.collapsedSummary.el);
			} else {
				this.collapsedSummary.update(collapsedSummaryData);
			}
		} else {
			this.collapsedSummary?.el.remove();
			delete this.collapsedSummary;
		}
	}

	private onCollapseExpandButtonClick = () => {
		if (!this.data) return;

		const { view, node } = this.data;
		setCollapsed(view, node, !isCollapsed(view.state, node));
	};

	private mobileCollapseExpandButton?: CollapseExpandButton;
	private updateMobileCollapseExpandButton(data: HeaderComponentData) {
		if (
			!this.maxMdMedia.matches ||
			!isCollapsible(data.view.state, data.node) ||
			getContext(data.view.state)?.inTemplate ||
			data.inNoteHistory
		) {
			if (this.mobileCollapseExpandButton) {
				this.mobileCollapseExpandButton.el.remove();
				delete this.mobileCollapseExpandButton;
			}
		} else {
			const buttonData = {
				collapsed: isCollapsed(data.view.state, data.node),
				onClick: this.onCollapseExpandButtonClick,
				className: styles.mobileCollapseExpandButton,
			};

			if (!this.mobileCollapseExpandButton) {
				this.mobileCollapseExpandButton = new CollapseExpandButton(buttonData);
				this.contentWrapper.after(this.mobileCollapseExpandButton.el);
			} else {
				this.mobileCollapseExpandButton.update(buttonData);
			}
		}
	}

	private ensureDOMStructure = () => {
		this.contentWrapper.append(this.boltIconContainer, this.contentDOM);
		// this.dom.append(this.boltIconContainer, this.contentWrapper);
		this.dom.appendChild(this.contentWrapper);
	};

	private showHideBoltIcon = (data: HeaderComponentData) => {
		let shouldShow = false;
		if (
			promptHeadingsEnabled &&
			!data.promptDisabled &&
			promptEnabled(data.node) &&
			(promptAllRequired(data.node) || promptExceptionUsers(data.node).length > 0)
		) {
			shouldShow = true;
		}
		if (!shouldShow) {
			this.boltIconContainer.style.display = "none";
		} else {
			this.boltIconContainer.style.display = "block";
		}

		// Update lightning bolt tooltip
		clearTooltip(this.boltIconContainer);
		const currentUser = getCurrentUser(data.view.state);
		const requiredUsers = sortBy(promptRequiredUsers(data.node, data.view.state), u =>
			u.id === currentUser?.id ? -1 : 0,
		);
		const userNames = requiredUsers.map(u => (u.id === currentUser?.id ? "You" : userName(u))).filter(Boolean);
		if (userNames.length > 4) {
			const diff = userNames.length - 3;
			const moreUsers = `${diff} others`;
			userNames.splice(3, diff, moreUsers);
		}
		let tooltipText;
		if (userNames.length) {
			const isOrAre = requiredUsers.length === 1 && requiredUsers[0].id !== currentUser?.id ? "is" : "are";
			tooltipText = `${listFormat(userNames).join("")} ${isOrAre} required to add to this section`;
		} else {
			tooltipText = "All responses are optional";
		}
		const tooltipAttrs = setTooltip(tooltipText, {
			width: "10rem",
			position: "bottom left",
			delayed: true,
		});
		setAttr(this.boltIconContainer, tooltipAttrs);
		if (tooltipAttrs.style) setStyles(this.boltIconContainer, tooltipAttrs.style);
	};

	private addPlusButton = (data: HeaderComponentData) => {
		if (!promptHeadingsEnabled || data.promptDisabled) return;
		this.plusButton?.el.remove();
		if (!promptEnabled(data.node) || getContext(data.view.state).inAgendaBuilderMode || !data.view.editable) return;
		if (!this.plusButton) this.plusButton = new PlusButton(data);
		this.plusButton.update(data);
		this.dom.appendChild(this.plusButton.el);
	};

	private addBoltButton = (data: HeaderComponentData) => {
		if (!promptHeadingsEnabled || data.promptDisabled) return;
		this.boltButton?.el.remove();
		if (promptAllRequired(data.node) || !getContext(data.view.state).inAgendaBuilderMode || !promptEnabled(data.node))
			return;
		if (!this.boltButton) this.boltButton = new BoltButton(data);
		this.boltButton.update(data);
		this.dom.appendChild(this.boltButton.el);
	};

	private updateBlockActions = (data: HeaderComponentData) => {
		if (data.inTemplate && !data.inCustomSection) {
			this.removeBlockActions();
			return this.addBlockActionsForTemplate(data);
		}
		if (data.inCustomSection) {
			this.removeBlockActions();
			return;
		}
		if (!this.shouldAddBlockActions(data)) return this.removeBlockActions();
		this.removeBlockActions();
		this.addBlockActions(data);
	};

	private shouldAddBlockActions = (data: HeaderComponentData) => {
		const { view, node } = data;
		return !(isVisuallyEmpty(node) || !blockActionsEnabled(view.state));
	};

	private addBlockActionsForTemplate = (data: HeaderComponentData) => {
		const { view, node, getPos, customSectionConfigs } = data;
		const { dom } = this;
		if (!view.editable) return;

		if (typeof getPos === "boolean") throw new Error("getPos is a bool for a node");
		const blockActions = new BlockActionMenu({
			node,
			view,
			menuSections: () => {
				const sections: AssignableDropdownMenuSections = [];
				if (promptHeadingsEnabled && !data.promptDisabled) {
					sections.splice(0, 0, {
						children: getPromptMenuItems(view, node, getPos, data.inTemplate, customSectionConfigs),
					});
				}
				return sections;
			},
			triggers: [],
		});
		this.blockActions = blockActions;
		dom.appendChild(blockActions.el);
	};

	private removeBlockActions = () => {
		if (this.blockActions) {
			this.blockActions.deinit();
			this.blockActions.el.remove();
		}
	};

	private addBlockActions = (data: HeaderComponentData) => {
		const { view, node, getPos, customSectionConfigs } = data;
		const { dom } = this;
		const context = getContext(view.state);
		const { creator: creatorId, blockId } = node.attrs;
		const creator = context?.participants?.find((p: { id: string }) => p.id === creatorId);

		if (typeof getPos === "boolean") throw new Error("getPos is a bool for a node");
		const blockActions = new BlockActionMenu({
			node,
			view,
			menuSections: () => {
				const sections: AssignableDropdownMenuSections = [
					{
						children: context.isMeetingModal
							? [getReactionMenuItem({ blockKey: blockId, editorView: view })]
							: !context.isPrivateNote
								? [
										getReactionMenuItem({ blockKey: blockId, editorView: view }),
										getCommentMenuItem({ view, blockKey: blockId }),
									]
								: [],
					},
					{
						children: [
							...(context?.noteId ? [getCopyLinkMenuItem({ noteId: context.noteId, blockKey: blockId })] : []),
							getDeleteBlockMenu({ view, getPos, text: "Delete" }),
						],
					},
					{ children: !context?.isPrivateNote ? [getCreatorMenuItem({ user: getUser(), creator })] : [] },
				];
				if (promptHeadingsEnabled && !data.promptDisabled) {
					sections.splice(0, 0, {
						children: getPromptMenuItems(view, node, getPos, data.inTemplate, customSectionConfigs),
					});
				}
				return sections;
			},
			triggers: [],
		});
		this.blockActions = blockActions;
		dom.appendChild(blockActions.el);
	};
}

/**
 * Check if a block is "empty".
 *
 * For our purposes, we extend isVisuallyEmpty to consider empty assignable
 * blocks as empty too.
 */
function blockIsEmpty(node: ProseMirrorNode): boolean {
	if (isVisuallyEmpty(node)) return true;
	if (node.type.name === "horizontal_rule") {
		return true;
	}
	if (node.type.name === "unstyled") {
		return node.textContent.trim().length === 0;
	}
	if (ASSIGNABLE_TYPE_NAMES.includes(node.type.name) || LIST_ITEM_TYPE_NAMES.includes(node.type.name)) {
		return node.content.size === 0;
	}
	return false;
}

interface CollapsedHeaderSummaryData {
	node: ProseMirrorNode;
	view: EditorView;
}

class CollapsedHeaderSummary implements DOMComponent<CollapsedHeaderSummaryData> {
	el: HTMLElement;
	private readonly expandButton: HTMLButtonElement;

	constructor(data: CollapsedHeaderSummaryData) {
		this.el = document.createElement("div");
		this.el.classList.add(styles.collapsedHeaderSummary);
		this.el.contentEditable = "false";

		this.expandButton = document.createElement("button");
		this.expandButton.innerText = "Expand section";
		this.expandButton.classList.add(styles.expandSectionButton);
		this.expandButton.onmousedown = e => e.preventDefault();
		this.expandButton.onclick = () => {
			setCollapsed(data.view, data.node, !isCollapsed(data.view.state, data.node));
		};

		this.el.append(this.expandButton);

		this.update(data);
	}

	update(data: CollapsedHeaderSummaryData) {
		const blockBoundary = getBlockBoundaryState(data.view.state);
		if (!blockBoundary) return;
		const tree = blockBoundary.blockTree.getNodeByBlockKey(data.node.attrs.blockId);
		if (!tree) return;

		// Clear out the summary text, we'll completely repopulate it
		while (this.expandButton.nextElementSibling) {
			this.el.removeChild(this.expandButton.nextElementSibling);
		}

		let numItems = 0,
			numTalkingPoints = 0,
			numCompletedTalkingPoints = 0,
			numActionItems = 0,
			numCompletedActionItems = 0;
		for (const block of tree.containedBlocks()) {
			if (block === tree.block) continue;
			if (blockIsEmpty(block)) {
				continue;
			}
			numItems += 1;
			if (block.type.name === "agenda") {
				numTalkingPoints += 1;
				if (block.attrs.done) {
					numCompletedTalkingPoints += 1;
				}
			} else if (block.type.name === "todo" && !block.attrs.wontDo) {
				numActionItems += 1;
				if (block.attrs.done) {
					numCompletedActionItems += 1;
				}
			}
		}
		if (numTalkingPoints) {
			const segment = this.newSummarySegment();
			segment.innerText = `${numCompletedTalkingPoints}/${numTalkingPoints} Talking points`;
			this.el.append(segment);
		}
		if (numActionItems) {
			const segment = this.newSummarySegment();
			segment.innerText = `${numCompletedActionItems}/${numActionItems} Action items`;
			this.el.append(segment);
		}
		if (!numTalkingPoints && !numActionItems) {
			const segment = this.newSummarySegment();
			segment.innerText = `${numItems} item${numItems === 1 ? "" : "s"}`;
			this.el.append(segment);
		}
	}

	private newSummarySegment(): HTMLSpanElement {
		const segment = document.createElement("span");
		segment.classList.add(styles.summarySegment);
		return segment;
	}
}

export function getHeaderTextSize(node: ProseMirrorNode): number {
	invariant(node.type.name === "header-one");
	let headingTextSize = 0;
	for (let i = 0; i < node.childCount; i += 1) {
		const child = node.child(i);
		if (child.type.name === "hard_break") break;
		headingTextSize += child.nodeSize;
	}
	return headingTextSize;
}

export function posInSubheading($pos: ResolvedPos): boolean {
	invariant($pos.parent.type.name === "header-one");
	// The offset into the header is greater than the size of the text node with
	// the header text. Subheadings are further text nodes after the first
	// hard_break node.
	return $pos.parentOffset > getHeaderTextSize($pos.parent);
}

/**
 * This function handles every enter keypress within a header-one node.
 */
function handleEnterInHeader(state: EditorState, dispatch?: (tr: Transaction) => void) {
	const { blockTree } = getBlockBoundaryState(state) ?? {};
	if (!blockTree) return false;

	const { selection } = state;
	const { $from, $to, empty } = selection;
	if ($from.depth === 0 || !$from.parent.isBlock || !$to.sameParent($from)) {
		return false;
	}
	if ($from.parent.type.name !== "header-one") {
		return false;
	}

	const headingBlock = $from.parent,
		headingBlockTreeNode = blockTree.getNodeByBlockKey(headingBlock.attrs.blockId);
	if (!headingBlockTreeNode) return false;

	if (dispatch) {
		const atStartAndEmpty = empty && $from.parentOffset === 0,
			// It's fine if the selection isn't empty
			inSubheading = posInSubheading($from) || posInSubheading($to);

		const sectionIsCollapsed = isCollapsed(state, headingBlock);

		const { tr } = state;
		if (atStartAndEmpty) {
			const newUnstyled = state.doc.type.schema.nodes.unstyled.createChecked({ blockId: v4() });
			tr.insert($from.before(1), newUnstyled);
		} else if (inSubheading) {
			// Section should already be expanded (otherwise subheading wouldn't be
			// visible). Split the subheading on the selection, putting the remainder
			// of the subheading in a new unstyled under the heading. This also runs
			// when you have an empty selection at the end of the subheading.
			invariant(!sectionIsCollapsed);
			const remainder = headingBlock.slice($to.parentOffset);
			invariant(remainder.openStart === remainder.openEnd);
			tr.delete($from.pos, $to.end(1));
			const newUnstyled = state.doc.type.schema.nodes.unstyled.createChecked({ blockId: v4() }, remainder.content);
			const insertPos = tr.mapping.map($to.after(1));
			tr.insert(insertPos, newUnstyled);
			tr.setSelection(TextSelection.create(tr.doc, insertPos + 1));
		} else {
			// Expand current section, split heading text on the selection, and
			// insert the remainder of the heading text as a new heading underneath.
			// Note that the subheading is not affected. This also runs when you have
			// an empty selection at the end of the heading text. If the selection is
			// empty and ad the end of the main heading text, an unstyled block is
			// inserted instead of a heading.
			const mainTextSize = getHeaderTextSize(headingBlock);
			const remainder = headingBlock.slice($to.parentOffset, mainTextSize);
			tr.delete($from.pos, $from.start(1) + mainTextSize);
			// If you have an empty selection at the end of the main header text,
			// insert a new unstyled below instead another header-one.
			let newBlock;
			if (empty && $to.parentOffset === mainTextSize) {
				newBlock = state.schema.nodes.unstyled.createChecked({ blockId: v4() }, remainder.content);
			} else {
				newBlock = headingBlock.type.createChecked(
					{ blockId: v4(), level: headingBlock.attrs.level },
					remainder.content,
				);
			}
			const insertPos = tr.mapping.map($from.after(1));
			tr.insert(insertPos, newBlock);
			tr.setSelection(TextSelection.create(tr.doc, insertPos + 1));
		}

		dispatch(tr);
	}

	return true;
}

/**
 * A command to handle backspace being pressed at the start of a node after a
 * header-one node.
 *
 * When backspacing into a header, we want to skip the subheading. This means
 * that in a case like this (where | is the cursor):
 *
 * # my heading
 * # <small>the subheading</small>
 * |A paragraph after
 *
 * Pressing backspace will result in:
 *
 * # my heading|A paragraph after
 * # <small>the subheading</small>
 */
function handleBackspaceIntoHeader(state: EditorState, dispatch?: (tr: Transaction) => void) {
	const { $from, empty } = state.selection;
	if (!empty) return false;
	if ($from.depth === 0) return false;
	const startOfCurrentBlock = $from.start(1);
	if ($from.pos !== startOfCurrentBlock) return false;
	const prevBlock = state.doc.resolve($from.pos - 1).nodeBefore;
	if (!prevBlock || prevBlock.type.name !== "header-one") return false;

	if (dispatch) {
		const text = $from.node(1).content;
		let insertPos = startOfCurrentBlock - prevBlock.nodeSize;
		for (let i = 0; i < prevBlock.childCount; i += 1) {
			const child = prevBlock.child(i);
			if (child.type.name === "hard_break") break;
			insertPos += child.nodeSize;
		}
		const { tr } = state;
		tr.delete($from.before(1), $from.after(1));
		tr.insert(insertPos, text);
		tr.setSelection(TextSelection.create(tr.doc, insertPos));
		dispatch(tr);
	}
	return true;
}

/**
 * Skip over the hard_break node when pressing the right arrow at the end of a
 * collapsed heading node with subheading.
 *
 * Without this handler, you would need to press the right arrow key twice for
 * the cursor to move from the end of the heading to the next section, since
 * the first key press would move the selection to after the hard_break node
 * which has no discernible effect.
 */
function handleArrowRightInHeader(state: EditorState, dispatch?: (tr: Transaction) => void) {
	const { $from, empty } = state.selection;
	if (!empty) return false;
	if ($from.depth === 0) return false;
	const block = closestBlock($from);
	if (block.type.name !== "header-one") return false;
	if (!isCollapsed(state, block)) return false;
	if ($from.nodeAfter && $from.nodeAfter.type.name !== "hard_break") return false;
	const { blockTree } = getBlockBoundaryState(state) ?? {};
	if (!blockTree) return false;
	const treeNode = blockTree.getNodeByBlockKey(block.attrs.blockId);
	if (!treeNode) return false;

	if (dispatch && treeNode.nextSibling) {
		dispatch(state.tr.setSelection(TextSelection.create(state.doc, treeNode.nextSibling.start + 1)).scrollIntoView());
	}

	return true;
}

export class Header extends NodeExtension {
	headerPreviewDomNodes = new Map<string, HTMLElement>(); // headerId, domElement

	get name() {
		return "header-one";
	}

	get plugins() {
		const decorateAIHeader = new Plugin({
			props: {
				decorations: (state: EditorState) => {
					const view = this.editorView;
					const headerData: HeaderContentType[] = getContext(state)?.generatedHeaders ?? [];
					const doneGeneratingHeaders = getContext(state)?.doneGeneratingHeaders ?? false;
					const streamAISettingsUpdated = getContext(state)?.streamAISettingsUpdated ?? false;
					const { blockTree } = getBlockBoundaryState(state) ?? {};
					const dismissibleElementsContext = getContext(state)?.dismissibleElementsContext;

					if (!view || !blockTree || !getContext(state).inAgendaBuilderMode) return;

					if ((!doneGeneratingHeaders && headerData.length == 0) || streamAISettingsUpdated) {
						return DecorationSet.create(state.doc, [
							Decoration.widget(0, () => new LoadingHeader({ isFirstBlock: true }).el, { side: AI_HEADERS_DECO_ORDER }),
						]);
					}

					const headerBlockSizes: { [k: string]: number } = {};
					state.doc.descendants((node, _pos, _parent, _index) => {
						if (node.type.name.startsWith("header")) {
							const treeNode = blockTree.getNodeByBlockKey(node.attrs.blockId);
							invariant(treeNode, "Could not find block in block tree");
							headerBlockSizes[node.attrs.blockId] = treeNode.size;
						}

						return false;
					});

					let position = 0;
					const decorationCompData: AIHeaderData[] = headerData.map((header, _index) => {
						if (header.id in headerBlockSizes) {
							position += headerBlockSizes[header.id];
						}
						return { view, position, showTooltip: _index === 0, viewer: this.options.viewer, ...header };
					});

					let lastPos = -1;
					const decorations: Decoration[] = decorationCompData
						.filter(({ id }) => !(id in headerBlockSizes))
						.map((data, _index) => {
							lastPos = data.position;
							return Decoration.widget(
								data.position,
								() => {
									const dom = this.headerPreviewDomNodes.get(data.id) ?? document.createElement("div");
									ReactDOM.render(<AIHeader {...data} dismissibleElementsContext={dismissibleElementsContext} />, dom);
									this.headerPreviewDomNodes.set(data.id, dom);
									return dom;
								},
								{ side: AI_HEADERS_DECO_ORDER },
							);
						});
					if (!doneGeneratingHeaders) {
						decorations.push(
							Decoration.widget(lastPos, () => new LoadingHeader({ isFirstBlock: headerData.length === 0 }).el, {
								side: AI_HEADERS_DECO_ORDER,
							}),
						);
					}
					return DecorationSet.create(state.doc, decorations);
				},
			},
		});

		return [DecorateSubheader, decorateAIHeader];
	}

	keys() {
		return {
			Enter: handleEnterInHeader,
			Backspace: handleBackspaceIntoHeader,
			ArrowRight: handleArrowRightInHeader,
		};
	}

	view = (
		node: ProseMirrorNode,
		view: EditorView,
		getPos: getPosHandler,
		_decorations: readonly Decoration[],
	): NodeView => {
		const component = new HeaderComponent({
			node,
			view,
			getPos,
			inTemplate: this.options.inTemplate ?? false,
			inNoteHistory: this.options.inNoteHistory ?? false,
			inCustomSection: this.options.inCustomSection ?? false,
			promptDisabled: this.options.promptDisabled ?? false,
			customSectionConfigs: {
				viewer: this.options.viewer,
			},
		});
		const { contentDOM, dom, contentWrapper } = component;

		return {
			dom,
			contentDOM,
			update: (newNode: ProseMirrorNode, _newDecorations: readonly Decoration[]) => {
				const newComponentData = {
					node: newNode,
					view,
					getPos,
					inTemplate: this.options.inTemplate ?? false,
					inNoteHistory: this.options.inNoteHistory ?? false,
					inCustomSection: this.options.inCustomSection ?? false,
					promptDisabled: this.options.promptDisabled ?? false,
					customSectionConfigs: {
						viewer: this.options.viewer,
					},
				};
				if (component.canUpdate(newComponentData)) {
					component.update(newComponentData);
					return true;
				}
				return false;
			},
			destroy() {
				component.deinit();
			},
			ignoreMutation: (
				record:
					| MutationRecord
					| {
							type: "selection";
							target: Element;
					  },
			) => {
				if (record.type === "selection" && contentDOM === record.target) {
					return false;
				}
				if (record.type !== "selection" && !contentWrapper?.contains(record.target)) {
					return true;
				}
				if (record.type == "characterData" || record.type == "childList") {
					return false;
				}
				return true;
			},
		};
	};

	get node() {
		return {
			content: "inline*",
			group: "block",
			attrs: {
				blockId: { default: null },
				creator: { default: null },
				level: { default: "1" },
				placeholder: { default: "" },
				...promptAttrs,
				...promptResponseAttrs,
			},
			defining: true,
			parseDOM: Array.from("123456").map(level => ({
				tag: `h${level}`,
				getAttrs: (dom: Node | string) => {
					if (dom instanceof HTMLElement) {
						return {
							level: dom.nodeName === "H1" ? "1" : "2",
							creator: dom.getAttribute("data-creator"),
							...deserializePromptAttrsFromDOMAttrs(dom),
						};
					}
					return null;
				},
				priority: 60,
			})),
			toDOM(node: ProseMirrorNode): DOMOutputSpec {
				return [
					`h${node.attrs.level}`,
					{
						"data-block-id": node.attrs["blockId"],
						"data-block-type": "header-one",
						"data-level": node.attrs.level,
						"data-creator": node.attrs.creator,
						...serializePromptAttrstoDOMAttrs(node),
					},
					0,
				];
			},
		};
	}

	menuEntry(_view: EditorView) {
		return [
			{
				icon: faH1,
				title: "Heading 1",
				subtitle: "Large section heading.",
				run: () => {
					return setBlockType(this.$type, { level: "1" });
				},
				section: "types" as const,
				priority: 4,
				active: (state: EditorState) => nodeActive(this.name, { level: "1" })(state),
				keyboardShortcut: "#",
			},
			{
				icon: faH2,
				title: "Heading 2",
				subtitle: "Medium section heading.",
				run: () => {
					return setBlockType(this.$type, { level: "2" });
				},
				section: "types" as const,
				priority: 3,
				active: (state: EditorState) => nodeActive(this.name, { level: "2" })(state),
				keyboardShortcut: "##",
			},
		];
	}

	inputRules() {
		return [blockTypeShortcut("#", this.$type, { level: "1" }), blockTypeShortcut("##", this.$type, { level: "2" })];
	}

	destroy(): void {
		this.headerPreviewDomNodes.forEach(dom => {
			ReactDOM.unmountComponentAtNode(dom);
			dom.remove();
		});
		this.headerPreviewDomNodes.clear();
	}
}

export default Header;
